/**
 * Shared SGR / FCR (planned) / projected-feed formulas.
 *
 * Extracted from `Feed.tsx`'s inline per-lot-per-week KPI calculation
 * (SGR ~L1871-1876, FCR ~L1878-1884, projected weekly feed ~L1824-1825) so
 * the `lotGrowthPerformance` Reports dataset can compute the exact same
 * numbers Feed.tsx shows, without a second, divergence-prone copy of the
 * math (docs/architecture-concerns.md I-6-style "one source of truth per
 * KPI" — the same pattern already followed for `computeCurrentBiomassPerLot`
 * and `src/utils/individualSampleMath.ts`).
 */

import { daysBetween } from './lotProjection'

export const computeProjectedWeeklyFeedKg = (biomassKg: number, sfrPercent: number): number => {
  const dailyFeedKg = (biomassKg * sfrPercent) / 100
  return dailyFeedKg * 7
}

/**
 * SGR = G = 100 × (e^g − 1) with g = (ln W2 − ln W1) / days (findings #213).
 * `days` may be fractional. Undefined when an input is not positive.
 */
export const computeSgrPercentFromWeights = (
  startWeightG: number,
  endWeightG: number,
  days: number
): number | undefined => {
  if (!(startWeightG > 0) || !(endWeightG > 0) || !(days > 0)) return undefined
  const g = (Math.log(endWeightG) - Math.log(startWeightG)) / days
  return 100 * (Math.exp(g) - 1)
}

export type SgrResult = {
  /** SGR = G = 100 × (e^g − 1): the real per cent change of mean weight per day. */
  sgrPercent: number
  /** Classic 100 × g, as most feed tables and papers report it (v2.2 findings.md #213). */
  sgrClassicPercent: number
  /** Calendar days between the two weight samples. */
  days: number
}

/**
 * SGR between two weight samples of the same lot (v2.2 findings.md #213).
 *
 * g = (ln W2 − ln W1) / days, with W the **mean weight** (g) of each sample
 * and days the real calendar days between the two sample dates
 * (YYYY-MM-DD, already in the farm's time zone). Biomass is never used: a
 * count, deaths or a move change the stock, not the growth.
 *
 * SGR is reported as G = 100 × (e^g − 1), the per cent change of weight
 * per day (Crane, Ogle & Shoup 2019, Reviews in Aquaculture,
 * doi 10.1111/raq.12396). The classic 100 × g is returned alongside so
 * reports can compare with feed tables and papers that use it.
 */
export const computeSgrFromSamples = (args: {
  startWeightG: number
  startDate: string
  endWeightG: number
  endDate: string
}): SgrResult | undefined => {
  if (!(args.startWeightG > 0) || !(args.endWeightG > 0)) return undefined
  if (!args.startDate || !args.endDate) return undefined
  const days = daysBetween(args.startDate, args.endDate)
  if (!Number.isFinite(days) || days <= 0) return undefined
  const g = (Math.log(args.endWeightG) - Math.log(args.startWeightG)) / days
  return {
    sgrPercent: 100 * (Math.exp(g) - 1),
    sgrClassicPercent: 100 * g,
    days
  }
}

/**
 * FCR (planned) = projected weekly feed (kg) / weight gained (kg). Callers
 * must only pass a `weightGainedKg` derived from two already-validated
 * (> 0) biomassKg readings — this mirrors Feed.tsx's own guard, which never
 * computes FCR unless both weeks' biomassKg are positive.
 */
export const computeFcrPlanned = (
  weeklyFeedKg: number,
  weightGainedKg: number
): number | undefined => {
  if (!(weeklyFeedKg > 0) || !(weightGainedKg > 0)) return undefined
  return weeklyFeedKg / weightGainedKg
}
